import { Loader } from "@googlemaps/js-api-loader";
import { MarkerConfig, MapMarkerType } from "./map-types";
import { createTextMarker } from "./TextMarker";
import {
	HHDSMapPopupHAlign,
	HHDSMapPopupVAlign,
} from "../../components/MapPopup/MapPopup";
import {
	DEFAULT_ZOOM_LEVEL,
	InitialiseMapOptions,
} from "../../components/LotMap/LotMap.constants";

const DEBUG_VERBOSE = false;
class MapHelper {
	// ----------------------------------------------------------------------
	// Map Configuraiton
	// ----------------------------------------------------------------------

	async createMap(options: InitialiseMapOptions): Promise<google.maps.Map> {
		const loader = new Loader({
			apiKey: options.apiKey,
			version: "weekly",
			libraries: ["geometry", "marker"],
		});
		await loader.load();

		const mapOptions = {
			center: options.center,
			disableDefaultUI: true,
			minZoom: options.minZoom || 10,
			maxZoom: options.maxZoom || 20,
			draggableCursor: "default", // Cursor when hovering over the map
			draggingCursor: "default", // Cursor while dragging the map
			mapId: options.mapId,
			scrollwheel: false,
			draggable: true,
			zoomControl:
				options.zoomControl !== undefined ? options.zoomControl : true,
			styles: options.styles,
		};
		const map = await new google.maps.Map(options.container, mapOptions);

		return map;
	}

	// ----------------------------------------------------------------------
	// Features
	// ----------------------------------------------------------------------

	getLayerBounds(layer: google.maps.Data): google.maps.LatLngBounds {
		const bounds = new google.maps.LatLngBounds();
		layer.forEach((feature: any) => {
			const geometry = feature.getGeometry();
			if (geometry) {
				const featureBounds = this.getFeatureBounds(feature);
				if (featureBounds) bounds.union(featureBounds);
			}
		});

		return bounds;
	}

	getCollectiveFeatureBounds(map: google.maps.Map): google.maps.LatLngBounds {
		const bounds = new google.maps.LatLngBounds();
		map.data.forEach((feature: any) => {
			const singleFeatureBounds = this.getFeatureBounds(feature);
			if (singleFeatureBounds) bounds.union(singleFeatureBounds);
		});
		return bounds;
	}

	getFeatureBounds(feature: any): google.maps.LatLngBounds | null {
		const geometry = feature.getGeometry();
		let bounds: google.maps.LatLngBounds | null = null;
		if (geometry.getType() === "MultiPolygon") {
			bounds = new google.maps.LatLngBounds();
			geometry.getArray().forEach((polygon: any) => {
				const ring = polygon?.getArray()[0]?.getArray(); // outer ring
				ring.forEach((latlng: any) => bounds!.extend(latlng));
			});
		} else if (geometry.getType() === "Polygon") {
			bounds = new google.maps.LatLngBounds();
			geometry
				.getArray()[0]
				.getArray()
				.forEach((latlng: any) => bounds!.extend(latlng));
		} else if (geometry.getType() === "Point") {
			const point = geometry.get();
			bounds = new google.maps.LatLngBounds(point, point);
		}
		return bounds;
	}

	getFeaturePosition(feature: any): google.maps.LatLng | null {
		const bounds = this.getFeatureBounds(feature);
		if (!bounds) return null;
		return bounds.getCenter();
	}

	zoomToFeature(feature: any, map: google.maps.Map) {
		const center = this.getFeaturePosition(feature);
		map.panTo(center!);
	}

	// ----------------------------------------------------------------------
	// Bounds
	// ----------------------------------------------------------------------

	getBounds(
		layer: google.maps.Data,
		focusedOnly: boolean = false
	): google.maps.LatLngBounds | null {
		if (!layer) {
			console.error("Map is not defined");
			return null;
		}

		const bounds = new google.maps.LatLngBounds();
		DEBUG_VERBOSE &&
			console.log(
				"GETTING BOUNDS FOR LOTS ==============================================="
			);
		let lotCount = 0;
		layer.forEach((feature: any) => {
			const lot = feature.getProperty("lot");

			// If this is an inactive lot, skip it
			if ((focusedOnly && !lot?.status) || lot?.status === "inactive") {
				return;
			}

			// If this is an active lot, add it to the bounds
			lotCount++;
			const geometry = feature.getGeometry();
			const extendBoundsFromGeometry = (geometry: any) => {
				if (geometry.getType() === "MultiPolygon") {
					geometry.getArray().forEach((polygon: any) => {
						const ring = polygon?.getArray()[0]?.getArray();
						ring.forEach((latlng: any) => bounds.extend(latlng));
					});
				} else if (geometry.getType() === "Polygon") {
					geometry
						.getArray()[0]
						?.getArray()
						.forEach((latlng: any) => bounds.extend(latlng));
				} else if (geometry.getType() === "Point") {
					bounds.extend(geometry.get());
				}
			};

			extendBoundsFromGeometry(geometry);
		});

		DEBUG_VERBOSE &&
			console.log("- Processed " + lotCount + " lots in bounds calculation");

		return bounds;
	}

	getPaddedBounds(
		layer: google.maps.Data,
		focusedOnly: boolean = false
	): google.maps.LatLngBounds | null {
		let bounds = this.getBounds(layer, focusedOnly);
		if (!bounds) return null;

		// Add padding after all features have been processed
		const paddedBounds = new google.maps.LatLngBounds({
			north: bounds.getNorthEast().lat() + 0.00025,
			south: bounds.getSouthWest().lat() - 0.00025,
			east: bounds.getNorthEast().lng() + 0.00025,
			west: bounds.getSouthWest().lng() - 0.00025,
		});

		return paddedBounds;
	}

	// ----------------------------------------------------------------------
	// Camera positioning
	// ----------------------------------------------------------------------

	zoomToCenterOfMapBounds(map: google.maps.Map) {
		if (map) {
			const bounds = map.getBounds();

			if (bounds) {
				const center = bounds.getCenter();
				const zoomLevel = 20;

				map.setCenter(center);
				map.setZoom(zoomLevel);
				DEBUG_VERBOSE &&
					console.log(`Zooming to center at ${center} with zoom ${zoomLevel}`);
			}
		}
	}

	zoomToCenterOfLayerBounds(
		layer: google.maps.Data,
		zoom: number = DEFAULT_ZOOM_LEVEL
	) {
		const map = layer.getMap();
		if (map) {
			const bounds = new google.maps.LatLngBounds();
			layer.forEach((feature) => {
				feature.getGeometry()?.forEachLatLng((latlng) => {
					bounds.extend(latlng);
				});
			});

			const center = bounds.getCenter();
			map.moveCamera({
				center: center,
				zoom: zoom,
			});
		}
	}

	// ----------------------------------------------------------------------
	// Markers
	// ----------------------------------------------------------------------

	addMarker(
		config: MarkerConfig
	):
		| google.maps.Marker
		| google.maps.marker.AdvancedMarkerElement
		| google.maps.OverlayView
		| undefined {
		const { type, feature, icon, label } = config;

		const position = this.getFeaturePosition(feature);
		if (position) {
			if (type === MapMarkerType.Text) {
				return this.addTextMarker(
					position,
					label,
					config.map as google.maps.Map,
					config.inactive
				);
			} else if (type === MapMarkerType.Icon) {
				return this.addIconMarker(
					position,
					icon,
					config.map as google.maps.Map
				);
			} else if (type === MapMarkerType.Badge) {
				return this.addBadgeMarker(
					position,
					label,
					config.map as google.maps.Map
				);
			}
			return undefined;
		}
	}

	addBadgeMarker(
		position: google.maps.LatLng,
		label: string = "",
		map: google.maps.Map
	): google.maps.marker.AdvancedMarkerElement | undefined {
		if (!position || !label || !map) {
			console.error("Position or label is missing for badge marker");
			return undefined;
		}

		const badgeEl = document.createElement("div");
		badgeEl.className = "hhds-map-badgemarker";
		badgeEl.textContent = label;

		const badgeMarker = new google.maps.marker.AdvancedMarkerElement({
			map: map,
			position,
			content: badgeEl,
		});

		return badgeMarker;
	}

	addIconMarker(
		position: google.maps.LatLng,
		icon: string = "",
		map: google.maps.Map
	): google.maps.Marker | undefined {
		if (!position || !icon || !map) {
			console.error("Position, icon or map is missing for IconMarker");
			return undefined;
		}

		// load the icon
		const iconUrl = new URL(icon, window.location.href);
		const iconElement = document.createElement("img");
		iconElement.src = iconUrl.href;
		iconElement.onload = () => {
			DEBUG_VERBOSE && console.log("Icon loaded successfully");
		};

		const size = new google.maps.Size(24, 24);
		const iconMarker = new google.maps.Marker({
			map: map,
			position,
			icon: {
				fillColor: "#FF0000",
				strokeColor: "#FF0000",
				url: icon,
				scaledSize: size,
				anchor: new google.maps.Point(size.width / 2, size.height / 2),
			},
		});

		return iconMarker;
	}

	addTextMarker(
		position: google.maps.LatLng,
		label: string = "",
		map: google.maps.Map,
		inactive: boolean = false
	): google.maps.OverlayView | undefined {
		if (!position || !label || !map) {
			console.error("Position, label or map is missing for TextMarker");
			return undefined;
		}

		const textEl = document.createElement("div");
		textEl.className = "hhds-map-textmarker";
		textEl.textContent = label;

		const textMarker = createTextMarker(position, label, inactive);
		textMarker.setMap(map);
		return textMarker;
	}

	// ----------------------------------------------------------------------
	// Popups
	// ----------------------------------------------------------------------

	popupHtml(
		hAlign: HHDSMapPopupHAlign,
		vAlign: HHDSMapPopupVAlign,
		zIndex: number = 10
	): string {
		const colSpans: string = `col-span-6 sm:col-span-5 md:col-span-4 lg:col-span-4`;
		let colStarts: string = "";
		if (hAlign == HHDSMapPopupHAlign.right) {
			colStarts = `sm:col-start-4 md:col-start-9 lg:col-start-9`;
		}

		return `
      <div class="grid" style="pointer-events: none; z-index: ${zIndex};">  
        <div class="${colSpans} ${colStarts}">
          <hhds-map-popup v-align="${vAlign}" style="--map-popup-height: 100%;"></hhds-map-popup>
        </div>
      </div>
    `;
	}
}

export const mapHelper = new MapHelper();
